import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

export type SlideTransitionPhase = "idle" | "fadeOut" | "fadeIn";

type UseSlideTransitionParams = {
  activeSlideIndex: number;
  setActiveSlideIndex: Dispatch<SetStateAction<number>>;
  durationMs: number;
};

type UseSlideTransitionResult = {
  slideTransitionPhase: SlideTransitionPhase;
  transitionToSlide: (nextIndex: number) => void;
  resetTransition: () => void;
};

export const useSlideTransition = ({
  activeSlideIndex,
  setActiveSlideIndex,
  durationMs,
}: UseSlideTransitionParams): UseSlideTransitionResult => {
  const [slideTransitionPhase, setSlideTransitionPhase] =
    useState<SlideTransitionPhase>("idle");

  const slideTransitionSessionRef = useRef(0);
  const fadeOutTimeoutRef = useRef<number | null>(null);
  const fadeInTimeoutRef = useRef<number | null>(null);

  const clearSlideTransitionTimers = useCallback(() => {
    if (fadeOutTimeoutRef.current !== null) {
      window.clearTimeout(fadeOutTimeoutRef.current);
      fadeOutTimeoutRef.current = null;
    }

    if (fadeInTimeoutRef.current !== null) {
      window.clearTimeout(fadeInTimeoutRef.current);
      fadeInTimeoutRef.current = null;
    }
  }, []);

  const resetTransition = useCallback(() => {
    clearSlideTransitionTimers();
    setSlideTransitionPhase("idle");
  }, [clearSlideTransitionTimers]);

  const transitionToSlide = useCallback(
    (nextIndex: number) => {
      clearSlideTransitionTimers();

      if (nextIndex === activeSlideIndex) {
        setSlideTransitionPhase("idle");
        return;
      }

      const transitionSessionId = slideTransitionSessionRef.current + 1;
      slideTransitionSessionRef.current = transitionSessionId;
      setSlideTransitionPhase("fadeOut");

      fadeOutTimeoutRef.current = window.setTimeout(() => {
        if (slideTransitionSessionRef.current !== transitionSessionId) return;

        setActiveSlideIndex(nextIndex);
        setSlideTransitionPhase("fadeIn");

        fadeInTimeoutRef.current = window.setTimeout(() => {
          if (slideTransitionSessionRef.current !== transitionSessionId) return;
          setSlideTransitionPhase("idle");
          fadeInTimeoutRef.current = null;
        }, durationMs);

        fadeOutTimeoutRef.current = null;
      }, durationMs);
    },
    [activeSlideIndex, clearSlideTransitionTimers, durationMs, setActiveSlideIndex],
  );

  useEffect(
    () => () => {
      clearSlideTransitionTimers();
    },
    [clearSlideTransitionTimers],
  );

  return { slideTransitionPhase, transitionToSlide, resetTransition };
};
